import { createSlice, Slice } from '@reduxjs/toolkit'
import { ActionsObservable, ofType } from 'redux-observable'

import { DeserializedSavedFilter } from 'api/AccountActivityAPI/types'
import { DateRange, getDefaultRange } from 'components/DateRangePicker'
import {
  FilterableEntity,
  FilterGroup,
  FilterGroupsByPage,
  FilterPage,
  removeFilter as removeFilterUtil,
  serializeFilters
} from 'utils/filters'
import { segmentTrack, TrackType } from 'utils/tracking'

export interface FiltersState {
  loaded: boolean
  defaultFilterId: number | undefined
  savedFilters: DeserializedSavedFilter[]
  dateRange?: DateRange
  filterGroupsByPage: FilterGroupsByPage
  searchQuery: string
  debouncedSearchQuery: string
  activeFilterId?: number
  sharedFilters: DeserializedSavedFilter[]
  privateFilters: DeserializedSavedFilter[]
  filterQuery?: string
}

export const initialState: FiltersState = {
  loaded: false,
  defaultFilterId: undefined,
  savedFilters: [],
  filterGroupsByPage: {
    ACCOUNTS: [[]],
    ACTIVITIES: [[]]
  },
  dateRange: getDefaultRange(),
  searchQuery: '',
  debouncedSearchQuery: '',
  activeFilterId: undefined,
  sharedFilters: [],
  privateFilters: [],
  filterQuery: undefined
}

const isValidPage = (page: FilterPage) => FilterPage[page] !== undefined

export const filtersSlice: Slice<FiltersState> = createSlice({
  name: 'filters',
  initialState,
  reducers: {
    setSavedFilters: (state: FiltersState, { payload }: { payload: DeserializedSavedFilter[] }) => {
      const sharedFilters = payload
        .filter(filter => filter.shared)
        .sort((a, b) => a.name.localeCompare(b.name))
      const privateFilters = payload
        .filter(filter => !filter.shared)
        .sort((a, b) => a.name.localeCompare(b.name))
      return {
        ...state,
        loaded: true,
        savedFilters: payload,
        sharedFilters,
        privateFilters
      }
    },
    setDefaultFilterId: (state, { payload }) => ({
      ...state,
      defaultFilterId: payload
    }),
    setActiveFilterId: (state, { payload }) => ({
      ...state,
      activeFilterId: payload,
      filterQuery: undefined
    }),
    setFilterGroupsByPage: (state, { payload }) => ({
      ...state,
      filterGroupsByPage: payload,
      searchQuery: '',
      debouncedSearchQuery: ''
    }),
    changeFilterGroups: (
      state: FiltersState,
      { payload: { filterGroups } }: { payload: { filterGroups: FilterGroup[] } }
    ) => {
      const filterGroupsByPage = {
        [FilterPage.ACTIVITIES]: filterGroups,
        [FilterPage.ACCOUNTS]: filterGroups
      }

      const filterQuery = serializeFilters(filterGroupsByPage)
      return {
        ...state,
        filterGroupsByPage,
        searchQuery: '',
        debouncedSearchQuery: '',
        filterQuery
      }
    },
    removeFilter: (
      state: FiltersState,
      {
        payload: { page, groupIndex, filterIndex }
      }: { payload: { page: FilterPage; groupIndex: number; filterIndex: number } }
    ) => {
      const groups = state.filterGroupsByPage[page]
      const nextGroups = removeFilterUtil(groups, groupIndex, filterIndex)
      const filterGroupsByPage = { ...state.filterGroupsByPage, [page]: nextGroups }
      if (!isValidPage(page)) {
        return state
      }
      const filterQuery = serializeFilters(filterGroupsByPage)

      return {
        ...state,
        filterGroupsByPage,
        activeFilterId: undefined,
        filterQuery
      }
    },
    clearFilters: (state: FiltersState) => ({
      ...state,
      filterGroupsByPage: initialState.filterGroupsByPage,
      activeFilterId: 0,
      filterQuery: serializeFilters(initialState.filterGroupsByPage)
    }),
    setDateRange: (state: FiltersState, { payload }: { payload: DateRange | undefined }) => ({
      ...state,
      dateRange: payload
    }),
    setSearchQuery: (state: FiltersState, { payload }: { payload: string }) => ({
      ...state,
      // Remove emojis from search query
      searchQuery: payload.replace(/[\u{1F000}-\u{1F9FF}]/gu, '').trim(),
      activeFilterId: 0,
      filterGroupsByPage: initialState.filterGroupsByPage,
      filterQuery: serializeFilters(initialState.filterGroupsByPage)
    }),
    setDebouncedSearchQuery: (state: FiltersState, { payload }: { payload: string }) => ({
      ...state,
      // Remove emojis from search query
      debouncedSearchQuery: payload.replace(/[\u{1F000}-\u{1F9FF}]/gu, '')
    })
  }
})

export const filtersMiddleware = (
  action: ActionsObservable<{ type: string; page: number; filterGroups: FilterGroup[] }>
) => {
  // Track when a firmographic filter is added
  action.pipe(ofType('filters/changeFilterGroups')).subscribe((action: any) => {
    const {
      payload: { filterGroups, page }
    } = action || {}
    if (!filterGroups.length || !isValidPage(page)) {
      return
    }
    const hasFirmographicFilter = filterGroups.length
      ? filterGroups.some((group: FilterGroup) => {
          return group.some(filter => filter.filterType === FilterableEntity.FIRMOGRAPHICS)
        })
      : false
    if (hasFirmographicFilter) {
      segmentTrack(TrackType.FIRMOGRAPHIC_FILTER_ADDED, {
        filterGroups: JSON.stringify(filterGroups)
      })
    }

    const hasLastTouched = filterGroups.length
      ? filterGroups.some((group: FilterGroup) => {
          return group.some(filter => filter.filterType === FilterableEntity.LAST_TOUCHED)
        })
      : false

    if (hasLastTouched) {
      segmentTrack(TrackType.LAST_TOUCH_FILTER_USE, {
        filterGroups: JSON.stringify(filterGroups)
      })
    }
  })
}

const { reducer: FiltersReducer, actions } = filtersSlice

export const {
  setSavedFilters,
  setDefaultFilterId,
  setFilterGroupsByPage,
  setDateRange,
  changeFilterGroups,
  clearFilters,
  removeFilter,
  setSearchQuery,
  setActiveFilterId,
  setDebouncedSearchQuery
} = actions

export default FiltersReducer
